import React from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, formValueSelector } from 'redux-form'
import { bindActionCreators } from 'redux'
import { SubmissionError } from 'redux-form'

import { addUniversalFilteringUpdateApp } from '../actions/universalupdates'
import { fetchMachines } from '../actions/machines'


class UniversalUpdateFilteringAppAddForm extends React.Component {
  constructor(props) {
    super(props)
    this.submit = this.submit.bind(this)
  }

  componentDidMount() {
    const { role, type } = this.props
    // Don't even load the machine list when the user is not ADMIN + MASTER
    if (role === 'ADMIN' && type === 'MASTER') {
      this.props.fetchMachines()
    }
  }

  submit(values) {
    // Global block: only ADMIN + MASTER can launch universal updates
    const { role, type } = this.props
    if (role !== 'ADMIN' || type !== 'MASTER') {
      throw new SubmissionError({
        _error: 'Acceso restringido: solo administradores MASTER pueden ejecutar actualizaciones universales.',
      })
    }

    const { arguments: url, ...rest } = values

    const selectedMachineIds = Object.entries(rest)
      .filter(([key, value]) => key.startsWith('machine_') && value)
      .map(([key]) => key.replace('machine_', ''))

    // Safety net: updates must always target a single OS (Ubuntu = OS 2, Raspbian otherwise)
    const { machines } = this.props
    const osSet = new Set(
      selectedMachineIds.map(id => {
        const machine = machines.find(m => m._id === id)
        return machine && machine.OS === 2 ? 'ubuntu' : 'raspbian'
      })
    )
    if (osSet.size > 1) {
      throw new SubmissionError({
        _error: 'Todas las máquinas seleccionadas deben tener el mismo sistema operativo — no se puede mezclar Ubuntu y Raspbian en una misma actualización.',
      })
    }

    // Same URL restriction as the remote-action form (CriticalActionAddForm):
    // the APK path must match the target OS — never run a Raspbian build on Ubuntu or vice versa
    if (osSet.has('ubuntu') && !(url || '').includes('/Ubuntu/')) {
      throw new SubmissionError({
        _error: 'Las máquinas seleccionadas usan Ubuntu. La URL debe contener /Ubuntu/',
      })
    }
    if (osSet.has('raspbian') && !(url || '').includes('/Raspbian/')) {
      throw new SubmissionError({
        _error: 'Las máquinas seleccionadas usan Raspbian. La URL debe contener /Raspbian/',
      })
    }

    return this.props.addUniversalFilteringUpdateApp({ url, machines: selectedMachineIds })
      .then(response => {
        if (response.error) {
          throw new SubmissionError({
            _error: response.error.message,
            ...response.error.fields
          })
        }

        // Success: confirmation toast + clear the selection and the URL
        if (this.props.onSuccess) {
          this.props.onSuccess(`Actualización de App enviada a ${selectedMachineIds.length} máquina${selectedMachineIds.length !== 1 ? 's' : ''}`)
        }
        this.props.reset()

        return response
      })
  }

  renderMachineCheckbox(machine, selectedOS) {
    const isUbuntu = machine.OS === 2
    const machineOS = isUbuntu ? 'ubuntu' : 'raspbian'
    // Lock the other OS once a machine is selected: updates are single-OS only
    const osLocked = selectedOS != null && machineOS !== selectedOS

    return (
      <label
        key={machine._id}
        htmlFor={`machine_${machine._id}`}
        title={osLocked
          ? `Actualización limitada a máquinas ${selectedOS === 'ubuntu' ? 'Ubuntu' : 'Raspbian'} — deselecciona las demás para cambiar de SO`
          : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, cursor: osLocked ? 'not-allowed' : 'pointer', opacity: osLocked ? 0.4 : 1, transition: 'background 0.12s, opacity 0.12s' }}
        onMouseEnter={e => { if (!osLocked) e.currentTarget.style.background = '#f1f5f9' }}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <Field
          name={`machine_${machine._id}`}
          id={`machine_${machine._id}`}
          component="input"
          type="checkbox"
          disabled={osLocked}
          style={{ width: 15, height: 15, accentColor: '#1d4ed8', cursor: osLocked ? 'not-allowed' : 'pointer', flexShrink: 0 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{machine.Serial}</span>
          <span style={{ fontSize: 10, color: '#64748b' }}>
            <i className={`fa-brands ${isUbuntu ? 'fa-ubuntu' : 'fa-raspberry-pi'} mr-1`}></i>
            {isUbuntu ? 'Ubuntu' : 'Raspbian'} · v{machine.Sw_version}
          </span>
        </div>
      </label>
    )
  }

  render() {
    const { error, pristine, submitting, handleSubmit, machines, selectedMachinesMap, urlValue, role, type } = this.props

    // Global block: this form is exclusive to ADMIN + MASTER, wherever it is mounted
    if (role !== 'ADMIN' || type !== 'MASTER') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(220,38,38,0.3)' }}>
            <i className="fas fa-lock" style={{ color: '#fff', fontSize: 22 }}></i>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>Acceso restringido</div>
            <div style={{ fontSize: 13, color: '#64748b', maxWidth: 320, lineHeight: 1.6 }}>
              La actualización selectiva es exclusiva para administradores con perfil <strong>MASTER</strong>.
            </div>
          </div>
        </div>
      )
    }

    const hasUrl = !!(urlValue && urlValue.trim())

    const selectedMachines = machines.filter(
      m => selectedMachinesMap && selectedMachinesMap[`machine_${m._id}`]
    )
    const selectedCount = selectedMachines.length

    // OS lock: once a machine is selected, only machines of the same OS can join
    const selectedOS = selectedCount > 0
      ? (selectedMachines[0].OS === 2 ? 'ubuntu' : 'raspbian')
      : null

    // Live URL↔OS validation: the APK path must contain the segment of the target OS
    const requiredSegment = selectedOS === 'ubuntu' ? '/Ubuntu/' : selectedOS === 'raspbian' ? '/Raspbian/' : null
    const urlMatchesOS = !requiredSegment || (urlValue || '').includes(requiredSegment)
    const canExecute = selectedCount > 0 && hasUrl && urlMatchesOS && !submitting

    const machinesByCustomer = machines.reduce((acc, machine) => {
      const customer = machine.Customer?.Fullname || 'Sin Cliente'
      acc[customer] = acc[customer] || []
      acc[customer].push(machine)
      return acc
    }, {})

    return (
      <form onSubmit={handleSubmit(this.submit)}>

        {/* ── Action panel (top) ───────────────── */}
        <div style={{ borderRadius: 10, overflow: 'hidden', border: '2px solid #1d4ed8', boxShadow: '0 4px 20px rgba(29,78,216,0.12)', marginBottom: 24 }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <i className="fas fa-mobile-alt" style={{ fontSize: 18, color: '#fff' }}></i>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Actualización Selectiva de App</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 }}>
                {selectedCount === 0
                  ? 'Selecciona máquinas abajo y añade la URL de la APK'
                  : <><strong style={{ color: '#fff' }}>{selectedCount}</strong> máquina{selectedCount !== 1 ? 's' : ''} seleccionada{selectedCount !== 1 ? 's' : ''}</>
                }
              </div>
            </div>
            {selectedCount > 0 && (
              <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 800 }}>
                {selectedCount}
              </span>
            )}
          </div>

          {/* Body */}
          <div style={{ background: '#fff', padding: '18px 20px' }}>
            {/* URL input */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
                <i className="fas fa-link mr-1"></i> App URL
                {selectedOS && (
                  <span style={{ marginLeft: 8, textTransform: 'none', letterSpacing: 0, fontWeight: 700, color: hasUrl && !urlMatchesOS ? '#dc2626' : (selectedOS === 'ubuntu' ? '#e95420' : '#c51a4a') }}>
                    {hasUrl && !urlMatchesOS && <i className="fas fa-exclamation-triangle mr-1"></i>}
                    debe contener {requiredSegment}
                  </span>
                )}
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13, pointerEvents: 'none' }}>
                  <i className="fas fa-download"></i>
                </div>
                <Field
                  name="arguments"
                  component="input"
                  type="text"
                  placeholder="https://example.com/app-release.apk"
                  style={{
                    width: '100%', padding: '10px 12px 10px 36px',
                    borderRadius: 8, border: '1.5px solid #e2e8f0',
                    fontSize: 13, fontFamily: 'monospace', color: '#1e293b',
                    background: '#f8fafc', outline: 'none',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#1d4ed8'; e.target.style.boxShadow = '0 0 0 3px rgba(29,78,216,0.12)' }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none' }}
                />
              </div>
            </div>

            {/* Selected machines chips */}
            {selectedCount > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                  Máquinas destino
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedMachines.map(m => (
                    <span
                      key={m._id}
                      style={{ background: '#dbeafe', color: '#1e40af', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #bfdbfe' }}
                    >
                      <i className={`fa-brands ${m.OS === 2 ? 'fa-ubuntu' : 'fa-raspberry-pi'}`} style={{ fontSize: 11 }}></i>
                      <span>{m.Serial}</span>
                      <span style={{ fontWeight: 400, color: '#3b82f6', fontSize: 10 }}>v{m.Sw_version}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {error && <div className="alert alert-danger mb-3">{error}</div>}

            {/* Execute button — requires machines, a URL, and the URL matching the target OS */}
            <button
              type="submit"
              disabled={pristine || !canExecute}
              title={!hasUrl
                ? 'Introduce la URL de la APK para poder ejecutar'
                : !urlMatchesOS
                  ? `La URL debe contener ${requiredSegment} para las máquinas seleccionadas`
                  : undefined}
              style={{
                width: '100%', padding: '11px 0', borderRadius: 8, border: 'none',
                background: canExecute ? 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)' : '#e2e8f0',
                color: canExecute ? '#fff' : '#94a3b8',
                fontWeight: 700, fontSize: 14, cursor: canExecute ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s'
              }}
            >
              <i className="fas fa-bolt mr-2"></i>
              {submitting ? 'Ejecutando...' : `Ejecutar en ${selectedCount} máquina${selectedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>

        {/* ── Machine selector (bottom) ────────── */}
        <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          {/* Card header */}
          <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="fas fa-server" style={{ color: '#fff', fontSize: 14, opacity: 0.9 }}></i>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Seleccionar máquinas</span>
            {selectedOS && (
              <span
                title="Las actualizaciones se ejecutan siempre sobre un único sistema operativo"
                style={{ background: selectedOS === 'ubuntu' ? '#e95420' : '#c51a4a', color: '#fff', borderRadius: 20, padding: '1px 10px', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <i className={`fa-brands ${selectedOS === 'ubuntu' ? 'fa-ubuntu' : 'fa-raspberry-pi'}`}></i>
                Solo {selectedOS === 'ubuntu' ? 'Ubuntu' : 'Raspbian'}
              </span>
            )}
            {Object.keys(machinesByCustomer).length > 0 && (
              <span style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>
                {machines.length} máquinas · {Object.keys(machinesByCustomer).length} clientes
              </span>
            )}
          </div>

          {/* Card body */}
          <div style={{ background: '#f8fafc', padding: 16 }}>
            {Object.keys(machinesByCustomer).length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>
                <i className="fas fa-spinner fa-spin mr-2"></i>Cargando máquinas...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {Object.entries(machinesByCustomer)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([customer, customerMachines]) => (
                    <div
                      key={customer}
                      style={{ borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                    >
                      <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)', padding: '8px 12px', fontSize: 11, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="fas fa-building" style={{ fontSize: 10, opacity: 0.8 }}></i>
                        {customer}
                      </div>
                      <div style={{ padding: '6px 2px' }}>
                        {customerMachines.map(machine => this.renderMachineCheckbox(machine, selectedOS))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

      </form>
    )
  }
}

// Redux Form wrapper
UniversalUpdateFilteringAppAddForm = reduxForm({
  form: 'UniversalUpdateFilteringAppAddForm',
})(UniversalUpdateFilteringAppAddForm)

// Map state from Redux
const selector = formValueSelector('UniversalUpdateFilteringAppAddForm')

const mapStateToProps = (state) => {
  const machines = state.machines.machines || []

  const machineFieldNames = machines.map(m => `machine_${m._id}`)
  const selectedMachinesMap = machineFieldNames.length
    ? selector(state, ...machineFieldNames)
    : {}

  return {
    machines,
    selectedMachinesMap,
    urlValue: selector(state, 'arguments'),
    role: state.auth.role,
    type: state.auth.type,
  }
}

// Map dispatch actions
const mapDispatchToProps = (dispatch) => ({
  addUniversalFilteringUpdateApp: bindActionCreators(addUniversalFilteringUpdateApp, dispatch),
  fetchMachines: bindActionCreators(fetchMachines, dispatch),
})

// Connect Redux and export
export default connect(mapStateToProps, mapDispatchToProps)(UniversalUpdateFilteringAppAddForm)
